import React, { useEffect, useRef, useState } from "react";
import {
    Alert, Autocomplete, Box, Breadcrumbs, Button, CircularProgress, 
    Grid, Link, MenuItem, Paper, Snackbar, TextField, Typography, 
    useMediaQuery, useTheme, Stack, InputAdornment, CssBaseline, Divider
} from "@mui/material";
import {
    NavigateNext, ArrowBack, Save, Person, School, FamilyRestroom,
    Badge, Cake, AccountBalanceWallet
} from '@mui/icons-material';
import { Form, Formik } from "formik";
import * as yup from "yup";
import Header from "../../component/header/Header";
import { useNavigate, useParams } from "react-router-dom";

// --- Standardized UI Colors ---
const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6",
    inputBg: '#FFFDF0', 
    border: '#F0E6CC'
};

const EditStudentScreen = () => {
    const { id } = useParams();
    const navigation = useNavigate();
    const formikRef = useRef();

    // --- State Management ---
    const [classList, setClassList] = useState([]);
    const [parentList, setParentList] = useState([]);
    const [studentDetail, setStudentDetail] = useState(null);
    const [successEditStudent, setSuccessEditStudent] = useState(false);
    const [failEditStudent, setFailEditStudent] = useState(false);
    const [failErrorMsg, setFailErrorMsg] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    useEffect(() => {
        getStudentDetail();
        getBatchList();
        getParentList();
        localStorage.setItem("current_tab", "Student");
    }, [id]);

    // ----- API Calls -----
    const getBatchList = async () => {
        try {
            const res = await fetch(`https://www.geniusmileniaeducation.link/db-api/batch`);
            const json = await res.json();
            setClassList(json.data || []);
        } catch (err) { console.error(err); }
    };

    const getParentList = async () => {
        try {
            const res = await fetch(`https://www.geniusmileniaeducation.link/db-api/parents`);
            const json = await res.json();
            setParentList(json.data || []);
        } catch (err) { console.error(err); }
    };

    const getStudentDetail = async () => {
        setLoading(true);
        try {
            const res = await fetch(`https://www.geniusmileniaeducation.link/db-api/students/${id}`);
            const json = await res.json();
            setStudentDetail(json.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleEditStudent = async (values) => {
        setLoadingSubmit(true);
        try {
            const res = await fetch(`https://www.geniusmileniaeducation.link/db-api/students-temp/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            const json = await res.json();
            if (json.status === 1) {
                setSuccessEditStudent(true);
                setTimeout(() => navigation("/Portal/Students"), 1500);
            } else {
                setFailErrorMsg(json.message);
                setFailEditStudent(true);
            }
        } catch (err) {
            setFailEditStudent(true);
            setFailErrorMsg("Connection Error");
        } finally {
            setLoadingSubmit(false);
        }
    };

    const validationSchema = yup.object({
        parentId: yup.string().required("Parent is required"),
        fullname: yup.string().required("Student Name is required"),
        gender: yup.string().required("Gender is required"),
        class: yup.string().required("Class is required"),
        amount: yup.string().required("Package Amount is required")
    });

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" bgcolor={colors.sidebarBg}>
            <CircularProgress sx={{ color: colors.accent }} />
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: colors.white, m: 2, borderRadius: 5 }}>
                
                {/* --- Header --- */}
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                    <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
                        <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                        <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Students")} sx={{ cursor: 'pointer' }}>Students</Link>
                        <Typography color="text.primary">Edit Student</Typography>
                    </Breadcrumbs>
                </Stack>

                <Formik
                    innerRef={formikRef}
                    enableReinitialize={true}
                    initialValues={{
                        parentId: studentDetail?.parentId || "",
                        fullname: studentDetail?.fullname || "",
                        gender: studentDetail?.gender || "",
                        dateOfBirth: studentDetail?.dateOfBirth || "",
                        class: studentDetail?.class || "",
                        amount: studentDetail?.amount || "",
                        status: studentDetail?.status || "A",
                        course: []
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleEditStudent}
                >
                    {({ values, errors, touched, handleBlur, handleChange, setFieldValue, submitForm }) => (
                        <Form>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Header title="Edit Student Profile" subtitle={`Updating record for ${studentDetail?.fullname}`} />
                                <Button 
                                    variant="contained" 
                                    onClick={submitForm}
                                    startIcon={loadingSubmit ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                    disabled={loadingSubmit}
                                    sx={{ bgcolor: colors.accent, color: 'black', borderRadius: 2, px: 4, fontWeight: 'bold', '&:hover': { bgcolor: colors.accentDark } }}
                                >
                                    Update Profile
                                </Button>
                            </Stack>

                            <Grid container spacing={3}>
                                {/* --- LEFT: GUARDIAN & ACADEMIC (md=4) --- */}
                                <Grid item xs={12} md={4}>
                                    <Stack spacing={3}>
                                        {/* Guardian Card */}
                                        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 3 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                                <FamilyRestroom sx={{ color: colors.accent }} />
                                                <Typography variant="subtitle1" fontWeight="bold">Guardian & Status</Typography>
                                            </Stack>
                                            <Stack spacing={2.5}>
                                                <Autocomplete
                                                    options={parentList}
                                                    getOptionLabel={(option) => option.fullname || ""}
                                                    value={parentList.find(option => option.parentId === values.parentId) || null}
                                                    onChange={(e, newValue) => setFieldValue("parentId", newValue ? newValue.parentId : "")}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Guardian" size="small" sx={{ bgcolor: colors.inputBg }} />
                                                    )}
                                                />
                                                <TextField select fullWidth label="Account Status" name="status" size="small" value={values.status} onChange={handleChange} sx={{ bgcolor: colors.inputBg }}>
                                                    <MenuItem value="A">Active</MenuItem>
                                                    <MenuItem value="I">Inactive</MenuItem>
                                                </TextField>
                                            </Stack>
                                        </Paper>

                                        {/* Financial Card */}
                                        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 3 }}>
                                            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                                <AccountBalanceWallet sx={{ color: colors.accent }} />
                                                <Typography variant="subtitle1" fontWeight="bold">Financial Details</Typography>
                                            </Stack>
                                            <TextField 
                                                label="Package Amount" 
                                                name="amount" 
                                                size="small" 
                                                fullWidth
                                                value={values.amount} 
                                                onChange={handleChange}
                                                InputProps={{ startAdornment: <InputAdornment position="start">RM</InputAdornment> }}
                                                sx={{ bgcolor: colors.inputBg }}
                                            />
                                        </Paper>
                                    </Stack>
                                </Grid>

                                {/* --- RIGHT: PERSONAL & ACADEMIC (md=8) --- */}
                                <Grid item xs={12} md={8}>
                                    <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: `1px solid ${colors.border}` }}>
                                        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                            <Person sx={{ color: colors.textMain }} />
                                            <Typography variant="h6" fontWeight="bold">Personal Information</Typography>
                                        </Stack>
                                        
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField fullWidth label="Student Full Name" name="fullname" value={values.fullname} onChange={handleChange} size="small" sx={{ bgcolor: 'white' }} />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField select fullWidth label="Gender" name="gender" value={values.gender} onChange={handleChange} size="small" sx={{ bgcolor: 'white' }}>
                                                    <MenuItem value="Lelaki">Male</MenuItem>
                                                    <MenuItem value="Perempuan">Female</MenuItem>
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField type="date" fullWidth label="Date of Birth" name="dateOfBirth" value={values.dateOfBirth} onChange={handleChange} size="small" InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} />
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Divider sx={{ my: 2 }} />
                                                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                                    <School sx={{ color: colors.textMain }} />
                                                    <Typography variant="subtitle1" fontWeight="bold">Class Assignment</Typography>
                                                </Stack>
                                                <TextField select fullWidth label="Assign Batch" name="class" size="small" value={values.class} onChange={handleChange} sx={{ bgcolor: 'white' }}>
                                                    {classList.filter(val => val.status === "A").map((val) => (
                                                        <MenuItem key={val.batchId} value={val.batchId}>{val.batchName}</MenuItem>
                                                    ))}
                                                </TextField>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    Note: Changing the batch may require reassignment of specific courses in the enrollment section.
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>

                <Snackbar open={successEditStudent} autoHideDuration={4000} onClose={() => setSuccessEditStudent(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>Student updated successfully!</Alert>
                </Snackbar>
                <Snackbar open={failEditStudent} autoHideDuration={4000} onClose={() => setFailEditStudent(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>{failErrorMsg || "Failed to update student."}</Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default EditStudentScreen;